export function isDiscountLine(item) {
    return item.totalAmountMinorUnits < 0;
}
/**
 * The user's own name for the discount row (e.g. "Less AFSA levy"): the first
 * discount line's description, or undefined when it is blank or just the
 * default label. Surfaces use undefined to keep their generic "Discount" and
 * "Subtotal before discount" copy; a custom label also relabels that subtotal.
 */
export function customDiscountLabel(lineItems, defaultLabel) {
    const label = lineItems.find(isDiscountLine)?.description?.trim();
    return label && label.toLowerCase() !== defaultLabel.toLowerCase() ? label : undefined;
}
/** New-policy discounts carry their allocated VAT in the existing exact-VAT field. */
export function isAllocatedDiscount(item) {
    return isDiscountLine(item) && item.vatAmountMinorUnits != null;
}
/**
 * Captured VAT, including exact zero, takes precedence over rate rounding.
 * Supplier-expense lines carry the posted payable VAT so a rounded effective
 * rate cannot introduce a cent of drift. Other/legacy lines still use the rate.
 * Migrating every line writer to explicit VAT is a separate contract change.
 */
export function lineItemVatMinorUnits(item) {
    if (item.vatAmountMinorUnits != null)
        return item.vatAmountMinorUnits;
    if (item.vatRateBasisPoints <= 0)
        return 0;
    return Math.round((item.totalAmountMinorUnits * item.vatRateBasisPoints) / 10000);
}
export function calculateInvoiceTotals(lineItems) {
    const subtotalMinorUnits = lineItems.reduce((sum, item) => sum + item.totalAmountMinorUnits, 0);
    const { discountVat } = allocateInvoiceDiscounts(lineItems);
    const taxAmountMinorUnits = lineItems.reduce((sum, item, index) => sum + (isAllocatedDiscount(item) ? -discountVat[index] : lineItemVatMinorUnits(item)), 0);
    return { subtotalMinorUnits, taxAmountMinorUnits, totalMinorUnits: subtotalMinorUnits + taxAmountMinorUnits };
}
export function invoiceSubtotalBeforeDiscount(totals) {
    return totals.subtotalMinorUnits - totals.discountMinorUnits;
}
/**
 * A presentation breakdown, not a replacement for frozen invoice totals.
 * Pass authoritative totals for issued invoices (or the API's draft overlay).
 * Fees are the remainder after disbursements, just as fees VAT is the remainder
 * of authoritative tax. This preserves frozen amounts on subsequent renders.
 * Negative lines are discounts, never counted again as disbursements.
 */
export function deriveInvoiceBreakdown(lineItems, authoritativeTotals = calculateInvoiceTotals(lineItems)) {
    const { allocations } = allocateInvoiceDiscounts(lineItems);
    let feesDiscountMinorUnits = 0;
    let disbursementsDiscountMinorUnits = 0;
    let disbursementsDiscountVatMinorUnits = 0;
    let discountMinorUnits = 0;
    let disbursementsSubtotalMinorUnits = 0;
    let disbursementsVatMinorUnits = 0;
    for (const [index, item] of lineItems.entries()) {
        const allocation = allocations[index];
        if (item.isDisbursement) {
            disbursementsDiscountMinorUnits -= allocation.amountMinorUnits;
            disbursementsDiscountVatMinorUnits -= allocation.vatAmountMinorUnits;
        }
        else
            feesDiscountMinorUnits -= allocation.amountMinorUnits;
        if (isDiscountLine(item)) {
            discountMinorUnits += item.totalAmountMinorUnits;
            if (!isAllocatedDiscount(item))
                feesDiscountMinorUnits += item.totalAmountMinorUnits;
        }
        else if (item.isDisbursement) {
            disbursementsSubtotalMinorUnits += item.totalAmountMinorUnits;
            disbursementsVatMinorUnits += lineItemVatMinorUnits(item) - allocation.vatAmountMinorUnits;
        }
    }
    const feesAfterDiscountMinorUnits = authoritativeTotals.subtotalMinorUnits - disbursementsSubtotalMinorUnits - disbursementsDiscountMinorUnits;
    return {
        subtotalMinorUnits: authoritativeTotals.subtotalMinorUnits,
        taxAmountMinorUnits: authoritativeTotals.taxAmountMinorUnits,
        totalMinorUnits: authoritativeTotals.totalMinorUnits,
        discountMinorUnits,
        feesDiscountMinorUnits,
        disbursementsDiscountMinorUnits,
        disbursementsDiscountInclVatMinorUnits: disbursementsDiscountMinorUnits + disbursementsDiscountVatMinorUnits,
        disbursementsBeforeDiscountInclVatMinorUnits: disbursementsSubtotalMinorUnits + disbursementsVatMinorUnits - disbursementsDiscountVatMinorUnits,
        disbursementsAfterDiscountMinorUnits: disbursementsSubtotalMinorUnits + disbursementsDiscountMinorUnits,
        disbursementsSubtotalMinorUnits,
        disbursementsVatMinorUnits,
        feesVatMinorUnits: authoritativeTotals.taxAmountMinorUnits - disbursementsVatMinorUnits,
        feesAfterDiscountMinorUnits,
        feesSubtotalMinorUnits: feesAfterDiscountMinorUnits - feesDiscountMinorUnits,
        grossSubtotalMinorUnits: invoiceSubtotalBeforeDiscount({ ...authoritativeTotals, discountMinorUnits }),
        disbursementsInclVatMinorUnits: disbursementsSubtotalMinorUnits + disbursementsDiscountMinorUnits + disbursementsVatMinorUnits,
    };
}
/**
 * A VAT summary by kind and treatment, for layouts that print one.
 * Each kind's totals are `deriveInvoiceBreakdown`'s after-discount and VAT
 * figures; the zeroOrExempt row's net is summed from its lines and the standard
 * row is the remainder, so the rows always sum to the authoritative subtotal and
 * tax. The zeroOrExempt row's VAT is always 0, and the standard row carries the
 * kind's whole VAT: `allocateInvoiceDiscounts` pools every captured-VAT line of
 * a kind, so a rate-0 captured line can be given part of a discount's VAT
 * reduction that belongs to the standard lines. Grouping by treatment rather than rate keeps a supplier expense's
 * blended effective rate (e.g. 1497) in the standard row.
 * Legacy unallocated discount lines count under fees, as in the breakdown,
 * and never create a row on their own.
 */
export function deriveInvoiceVatCategories(lineItems, authoritativeTotals = calculateInvoiceTotals(lineItems)) {
    const breakdown = deriveInvoiceBreakdown(lineItems, authoritativeTotals);
    const { allocations } = allocateInvoiceDiscounts(lineItems);
    const kinds = [
        { kind: 'fees', netMinorUnits: breakdown.feesAfterDiscountMinorUnits, vatMinorUnits: breakdown.feesVatMinorUnits },
        { kind: 'disbursements', netMinorUnits: breakdown.disbursementsAfterDiscountMinorUnits, vatMinorUnits: breakdown.disbursementsVatMinorUnits },
    ];
    return kinds.flatMap(({ kind, netMinorUnits, vatMinorUnits }) => {
        let zeroNetMinorUnits = 0;
        let hasStandardLine = false;
        let hasZeroLine = false;
        for (const [index, item] of lineItems.entries()) {
            const isStandard = item.vatRateBasisPoints > 0;
            if (isDiscountLine(item)) {
                if (kind === 'fees' && !isStandard && !isAllocatedDiscount(item))
                    zeroNetMinorUnits += item.totalAmountMinorUnits;
                continue;
            }
            if (item.totalAmountMinorUnits === 0 || item.isDisbursement !== (kind === 'disbursements'))
                continue;
            if (isStandard) {
                hasStandardLine = true;
                continue;
            }
            hasZeroLine = true;
            zeroNetMinorUnits += item.totalAmountMinorUnits - allocations[index].amountMinorUnits;
        }
        if (!hasZeroLine)
            zeroNetMinorUnits = 0;
        const standardNetMinorUnits = netMinorUnits - zeroNetMinorUnits;
        const rows = [];
        if (hasStandardLine || standardNetMinorUnits !== 0 || vatMinorUnits !== 0) {
            rows.push({ kind, treatment: 'standard', netMinorUnits: standardNetMinorUnits, vatMinorUnits, grossMinorUnits: standardNetMinorUnits + vatMinorUnits });
        }
        if (hasZeroLine) {
            rows.push({ kind, treatment: 'zeroOrExempt', netMinorUnits: zeroNetMinorUnits, vatMinorUnits: 0, grossMinorUnits: zeroNetMinorUnits });
        }
        return rows;
    });
}
/** Semantic monetary rows shared by every invoice renderer; copy packages add labels. */
export function invoiceTotalsAmountRows(input) {
    const { lineItems, totals, documentType = 'invoice' } = input;
    const breakdown = deriveInvoiceBreakdown(lineItems, totals);
    const hasFees = lineItems.some((line) => !line.isDisbursement && !isDiscountLine(line));
    const hasDisbursements = lineItems.some((line) => line.isDisbursement && !isDiscountLine(line));
    const mixed = documentType === 'invoice' && hasFees && hasDisbursements;
    if (mixed) {
        const rows = [
            { key: 'feesSubtotal', amountMinorUnits: breakdown.feesSubtotalMinorUnits },
        ];
        if (breakdown.feesDiscountMinorUnits !== 0) {
            rows.push({ key: 'discount', amountMinorUnits: breakdown.feesDiscountMinorUnits }, { key: 'feesAfterDiscount', amountMinorUnits: breakdown.feesAfterDiscountMinorUnits });
        }
        rows.push({ key: 'feesVat', amountMinorUnits: breakdown.feesVatMinorUnits }, { key: 'disbursementsSubtotal', amountMinorUnits: breakdown.disbursementsSubtotalMinorUnits });
        if (breakdown.disbursementsDiscountMinorUnits !== 0) {
            rows.push({ key: 'disbursementsDiscount', amountMinorUnits: breakdown.disbursementsDiscountMinorUnits }, { key: 'disbursementsAfterDiscount', amountMinorUnits: breakdown.disbursementsAfterDiscountMinorUnits });
        }
        rows.push({ key: 'disbursementsVat', amountMinorUnits: breakdown.disbursementsVatMinorUnits }, { key: 'total', amountMinorUnits: totals.totalMinorUnits });
        return rows;
    }
    const firstKey = documentType === 'credit-note' || (!hasDisbursements && breakdown.discountMinorUnits >= 0)
        ? 'subtotalExcludingVat'
        : hasDisbursements
            ? 'disbursementsSubtotal'
            : 'grossSubtotal';
    const firstAmountMinorUnits = documentType === 'credit-note'
        ? totals.subtotalMinorUnits
        : breakdown.grossSubtotalMinorUnits;
    const rows = [{ key: firstKey, amountMinorUnits: firstAmountMinorUnits }];
    if (documentType === 'invoice' && breakdown.discountMinorUnits < 0) {
        rows.push({ key: 'discount', amountMinorUnits: breakdown.discountMinorUnits }, { key: 'subtotalExcludingVat', amountMinorUnits: totals.subtotalMinorUnits });
    }
    if (hasDisbursements && documentType === 'invoice') {
        rows.push({ key: 'feesVat', amountMinorUnits: breakdown.feesVatMinorUnits }, { key: 'disbursementsVat', amountMinorUnits: breakdown.disbursementsVatMinorUnits });
    }
    else {
        rows.push({ key: 'vat', amountMinorUnits: totals.taxAmountMinorUnits });
    }
    rows.push({ key: 'total', amountMinorUnits: totals.totalMinorUnits });
    return rows;
}
/** Distribute integer cents by weight, resolving remainder ties by stable source identity when supplied. */
function proportionalAmounts(amount, weights, keys) {
    const total = weights.reduce((sum, weight) => sum + weight, 0);
    if (total === 0)
        return weights.map(() => 0);
    const denominator = BigInt(total);
    const parts = weights.map((weight, index) => {
        const numerator = BigInt(amount) * BigInt(weight);
        return { index, amount: Number(numerator / denominator), remainder: numerator % denominator };
    });
    let remainder = amount - parts.reduce((sum, part) => sum + part.amount, 0);
    for (const part of [...parts].sort((a, b) => a.remainder === b.remainder ? (keys ? keys[a.index].localeCompare(keys[b.index]) : a.index - b.index) : a.remainder > b.remainder ? -1 : 1)) {
        if (remainder-- <= 0)
            break;
        part.amount++;
    }
    return parts.map((part) => part.amount);
}
function allocateVatReductions(lines, allocations) {
    const groups = new Map();
    lines.forEach((line, index) => {
        if (line.totalAmountMinorUnits <= 0)
            return;
        const category = line.isDisbursement ? 'disbursement' : 'fee';
        const vatTreatment = line.vatAmountMinorUnits != null ? 'captured' : `rate:${line.vatRateBasisPoints}`;
        const key = `${category}:${vatTreatment}`;
        const group = groups.get(key);
        if (group)
            group.push(index);
        else
            groups.set(key, [index]);
    });
    for (const indices of groups.values()) {
        const groupTotal = indices.reduce((sum, index) => sum + lines[index].totalAmountMinorUnits, 0);
        const groupAllocated = indices.reduce((sum, index) => sum + allocations[index].amountMinorUnits, 0);
        if (groupAllocated === 0)
            continue;
        const grossVat = indices.reduce((sum, index) => sum + lineItemVatMinorUnits(lines[index]), 0);
        const numerator = BigInt(grossVat) * BigInt(groupTotal - groupAllocated);
        const denominator = BigInt(groupTotal);
        const netVat = Number((numerator * 2n + denominator) / (denominator * 2n));
        const vatReductions = proportionalAmounts(grossVat - netVat, indices.map((index) => allocations[index].amountMinorUnits), indices.map((index) => lines[index].id ?? String(index)));
        indices.forEach((index, position) => {
            allocations[index].vatAmountMinorUnits = vatReductions[position];
        });
    }
}
/** One shared allocation for totals, VAT, documents and GL posting. Values are positive reductions. */
export function allocateInvoiceDiscounts(lines, allocateAllDiscounts = false, preserveStoredVat = false) {
    const usesAllocation = (line) => isDiscountLine(line) && (allocateAllDiscounts || isAllocatedDiscount(line));
    const discount = -lines.reduce((sum, line) => sum + (usesAllocation(line) ? line.totalAmountMinorUnits : 0), 0);
    let remaining = discount;
    const allocations = lines.map(() => ({ amountMinorUnits: 0, vatAmountMinorUnits: 0 }));
    for (const disbursements of [false, true]) {
        const indices = lines.flatMap((line, index) => line.totalAmountMinorUnits > 0 && Boolean(line.isDisbursement) === disbursements ? [index] : []);
        const weights = indices.map((index) => lines[index].totalAmountMinorUnits);
        const allocated = Math.min(remaining, weights.reduce((sum, weight) => sum + weight, 0));
        const amounts = proportionalAmounts(allocated, weights, indices.map((index) => lines[index].id ?? String(index)));
        indices.forEach((index, position) => {
            allocations[index].amountMinorUnits = amounts[position];
        });
        remaining -= allocated;
    }
    allocateVatReductions(lines, allocations);
    if (preserveStoredVat) {
        const frozenVatDiscount = -lines.reduce((sum, line) => sum + (usesAllocation(line) ? line.vatAmountMinorUnits ?? 0 : 0), 0);
        const computedVatDiscount = allocations.reduce((sum, allocation) => sum + allocation.vatAmountMinorUnits, 0);
        if (frozenVatDiscount !== computedVatDiscount) {
            const weights = computedVatDiscount === 0 ? allocations.map((allocation) => allocation.amountMinorUnits) : allocations.map((allocation) => allocation.vatAmountMinorUnits);
            const reconciledVat = proportionalAmounts(frozenVatDiscount, weights, lines.map((line, index) => line.id ?? String(index)));
            allocations.forEach((allocation, index) => {
                allocation.vatAmountMinorUnits = reconciledVat[index];
            });
        }
    }
    const vatDiscount = allocations.reduce((sum, allocation) => sum + allocation.vatAmountMinorUnits, 0);
    const discountVat = proportionalAmounts(vatDiscount, lines.map((line) => usesAllocation(line) ? -line.totalAmountMinorUnits : 0));
    return { allocations, discountVat, unallocatedDiscountMinorUnits: remaining };
}
/** Drafts adopt the current policy; issued legacy rows retain null exact VAT. */
export function prepareDraftInvoiceLines(lines) {
    const { discountVat } = allocateInvoiceDiscounts(lines, true);
    return lines.map((line, index) => isDiscountLine(line) ? { ...line, vatAmountMinorUnits: -discountVat[index] } : line);
}
